import { buildRecordDiscussionKey, chunkRecordIds, DATA_RECORD_ANNOTATION_TYPE } from "common/recordDiscussions";
import errorHandler from "common/errorHandler";
import { all, call, put, select, takeEvery } from "redux-saga/effects";
import { Endpoints } from "services/api";
import { CREATE_ANNOTATION_SUCCESS, DELETE_ANNOTATION_SUCCESS, UPDATE_ANNOTATION_SUCCESS } from "../annotations/actions";
import { ADD_COMMENT_SUCCESS, DELETE_COMMENT_SUCCESS } from "../comments/actions";
import * as actions from "./actions";

const RECORD_DISCUSSION_PREVIEW_LIMIT = 3;

const getRecordDiscussionsState = (state) => state.recordDiscussions;

function* getSummaryChunk(api, recordType, recordIds) {
    const params = new URLSearchParams({
        recordids: recordIds.join(","),
        annotationrecordtype: recordType,
        previewlimit: String(RECORD_DISCUSSION_PREVIEW_LIMIT),
    });
    const url = `${Endpoints.comments}/api/v1/records/discussion-summary?${params.toString()}`;
    const result = yield call([api, api.get], url);
    return result.data;
}

function* fetchSummaries(api, recordType, recordIds) {
    yield put(actions.recordDiscussionSummaryFetchStarted(recordType, recordIds));

    try {
        const chunks = chunkRecordIds(recordIds);
        const responses = yield all(chunks.map((chunk) => call(getSummaryChunk, api, recordType, chunk)));

        const summaries = responses.reduce((accumulated, response) => Object.assign(accumulated, (response && response.Items) || {}), {});

        yield put(actions.getRecordDiscussionSummarySuccess(recordType, recordIds, summaries));
    } catch (error) {
        const errorObject = errorHandler(error, "Get", "record discussion summary");
        yield put(actions.getRecordDiscussionSummaryFailure(recordType, recordIds, errorObject));
    }
}

export function* getRecordDiscussionSummaryRequestSaga(api, action) {
    const { recordType, recordIds } = action.payload;
    if (!recordType || !recordIds || recordIds.length === 0) {
        return;
    }

    const { summaryByKey, pendingKeys } = yield select(getRecordDiscussionsState);

    const missingRecordIds = Array.from(new Set(recordIds.filter(Boolean))).filter((recordId) => {
        const key = buildRecordDiscussionKey(recordType, recordId);
        return !summaryByKey[key] && !pendingKeys[key];
    });

    if (missingRecordIds.length === 0) {
        return;
    }

    yield call(fetchSummaries, api, recordType, missingRecordIds);
}

export function* refreshRecordDiscussionSummaryRequestSaga(api, action) {
    const { recordType, recordId } = action.payload;
    if (!recordType || !recordId) {
        return;
    }

    const { pendingKeys } = yield select(getRecordDiscussionsState);
    if (pendingKeys[buildRecordDiscussionKey(recordType, recordId)]) {
        return;
    }

    yield call(fetchSummaries, api, recordType, [recordId]);
}

export function* onCommentMutationSaga(action) {
    const recordId = action.payload && action.payload.recordId;
    if (!recordId) {
        return;
    }

    const { summaryByKey } = yield select(getRecordDiscussionsState);
    const suffix = `:${recordId}`;
    const cachedKeys = Object.keys(summaryByKey).filter((key) => key.endsWith(suffix));

    for (const key of cachedKeys) {
        const recordType = key.slice(0, key.length - suffix.length);
        yield put(actions.refreshRecordDiscussionSummaryRequest(recordType, recordId));
    }
}

export function* onAnnotationMutationSaga(action) {
    const recordKey = action.payload && action.payload.recordKey;
    if (!recordKey) {
        return;
    }

    const { summaryByKey } = yield select(getRecordDiscussionsState);
    if (!summaryByKey[recordKey]) {
        return;
    }

    const separatorIndex = recordKey.indexOf(":");
    const recordType = separatorIndex >= 0 ? recordKey.slice(0, separatorIndex) : DATA_RECORD_ANNOTATION_TYPE;
    const recordId = separatorIndex >= 0 ? recordKey.slice(separatorIndex + 1) : recordKey;

    yield put(actions.refreshRecordDiscussionSummaryRequest(recordType, recordId));
}

export default function* ({ api }) {
    yield takeEvery(actions.GET_RECORD_DISCUSSION_SUMMARY_REQUEST, getRecordDiscussionSummaryRequestSaga, api);
    yield takeEvery(actions.REFRESH_RECORD_DISCUSSION_SUMMARY_REQUEST, refreshRecordDiscussionSummaryRequestSaga, api);
    yield takeEvery([ADD_COMMENT_SUCCESS, DELETE_COMMENT_SUCCESS], onCommentMutationSaga);
    yield takeEvery([CREATE_ANNOTATION_SUCCESS, UPDATE_ANNOTATION_SUCCESS, DELETE_ANNOTATION_SUCCESS], onAnnotationMutationSaga);
}
